import { useState } from "react";
import axios from "axios";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  "Discovery / Pre-clinical",
  "Phase I",
  "Phase II",
  "Phase III",
  "Regulatory submission",
  "Commercial / Post-market",
  "Organizational scaling",
  "Investor diligence",
];

const inputCls =
  "w-full bg-white/[0.04] border border-white/15 focus:border-teal-400/70 focus:bg-white/[0.06] outline-none text-slate-100 placeholder:text-slate-500 text-sm px-5 py-3.5 transition-[border-color,background-color] duration-300";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", organization: "", stage: "", message: "" });
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/enquiries`, form);
      setStatus("sent");
      toast.success("Enquiry received. Dr. Aspeslet's office will be in touch.");
    } catch (err) {
      setStatus("idle");
      toast.error("Something went wrong sending your enquiry. Please try again.");
    }
  };

  return (
    <section id="contact" data-testid="contact-booking-section" className="bg-[#070D1D] py-24 lg:py-36 relative overflow-hidden">
      <div className="absolute bottom-0 left-1/4 w-[600px] h-[400px] rounded-full bg-teal-500/[0.07] blur-[130px] pointer-events-none" />
      <img
        src="/hummingbird.png"
        alt=""
        aria-hidden="true"
        className="absolute left-[-90px] top-1/2 -translate-y-1/2 w-[520px] max-w-none opacity-[0.05] pointer-events-none select-none"
        data-testid="contact-bird-watermark"
      />
      <div className="max-w-7xl mx-auto px-6 sm:px-10 relative">
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-20">
          <div className="lg:col-span-5">
            <Reveal>
              <Eyebrow dark>Begin the Conversation</Eyebrow>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.12] text-[#F8F9FA] mt-6">
                Have a complex life sciences challenge?
              </h2>
              <p className="mt-6 font-serif text-2xl text-teal-300 italic">
                Let's discuss the path forward.
              </p>
              <p className="mt-6 text-base text-slate-400 leading-relaxed max-w-md">
                Share a few details about your program, organization or decision at hand. Every
                enquiry is reviewed personally, and you can expect a considered response — not an
                automated one.
              </p>
              <div className="mt-10 space-y-3 font-mono text-xs uppercase tracking-[0.2em] text-slate-500">
                <p data-testid="contact-location">Edmonton, AB, Canada · Global</p>
                <p data-testid="contact-email">info@calliopeadvisory.ca</p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              {status === "sent" ? (
                <div className="border border-teal-500/30 bg-teal-950/20 p-10 sm:p-14 text-center" data-testid="contact-success-panel">
                  <CheckCircle2 className="w-10 h-10 text-teal-400 mx-auto" />
                  <h3 className="mt-6 font-serif text-3xl text-[#F8F9FA]">Thank you.</h3>
                  <p className="mt-4 text-slate-400 text-sm leading-relaxed max-w-md mx-auto">
                    Your enquiry has been received. Dr. Aspeslet's office will review it personally
                    and respond shortly to arrange a strategic conversation.
                  </p>
                  <button
                    data-testid="contact-send-another"
                    onClick={() => { setStatus("idle"); setForm({ name: "", email: "", organization: "", stage: "", message: "" }); }}
                    className="mt-8 text-teal-300 hover:text-teal-200 text-sm font-medium tracking-wide transition-colors"
                  >
                    Send another enquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="border border-white/10 bg-white/[0.02] p-8 sm:p-10 space-y-5" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <input required data-testid="contact-name-input" placeholder="Full name" value={form.name} onChange={set("name")} className={inputCls} />
                    <input required type="email" data-testid="contact-email-input" placeholder="Email address" value={form.email} onChange={set("email")} className={inputCls} />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <input data-testid="contact-organization-input" placeholder="Organization" value={form.organization} onChange={set("organization")} className={inputCls} />
                    <select required data-testid="contact-stage-select" value={form.stage} onChange={set("stage")} className={`${inputCls} appearance-none ${form.stage ? "" : "text-slate-500"}`}>
                      <option value="" disabled className="bg-[#0B132B]">Development stage</option>
                      {STAGES.map((s) => (
                        <option key={s} value={s} className="bg-[#0B132B] text-slate-100">{s}</option>
                      ))}
                    </select>
                  </div>
                  <textarea
                    required
                    rows={5}
                    data-testid="contact-message-input"
                    placeholder="Tell us about the challenge or decision in front of you"
                    value={form.message}
                    onChange={set("message")}
                    className={`${inputCls} resize-none`}
                  />
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    data-testid="contact-submit-button"
                    className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-teal-600 hover:bg-teal-500 disabled:opacity-60 text-white text-sm font-semibold tracking-wide px-8 py-4 transition-colors duration-300"
                  >
                    {status === "sending" ? (
                      <>Sending <Loader2 className="w-4 h-4 animate-spin" /></>
                    ) : (
                      <>Book a Strategic Conversation <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" /></>
                    )}
                  </button>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
